import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Badge,
    Table, Modal
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";

export default function DetailsReglement() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [reglement, setReglement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        fetchReglement();
    }, [id]);

    const fetchReglement = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/reglements.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setReglement(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleValider = async () => {
        setIsProcessing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/reglements.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'valider',
                        id: id
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowConfirmModal(false);
                fetchReglement();
            }
        } catch (err) {
            console.error("Erreur:", err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSupprimer = async () => {
        setIsProcessing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/reglements.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                navigate('/ventes/reglements');
            }
        } catch (err) {
            console.error("Erreur:", err);
        } finally {
            setIsProcessing(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            en_attente: { color: 'warning', label: 'En attente' },
            valide: { color: 'success', label: 'Validé' },
            rejete: { color: 'danger', label: 'Rejeté' }
        };
        const config = configs[statut] || configs.en_attente;
        return <Badge bg={config.color} className="px-3 py-2">{config.label}</Badge>;
    };

    const getModeIcon = (mode) => {
        const icons = {
            especes: 'ri-money-cash-line',
            cheque: 'ri-bank-card-line',
            virement: 'ri-bank-line',
            carte_bancaire: 'ri-smartphone-line',
            traite: 'ri-file-copy-line'
        };
        return icons[mode] || 'ri-bank-card-line';
    };

    const getModeLabel = (mode) => {
        const labels = {
            especes: 'Espèces',
            cheque: 'Chèque',
            virement: 'Virement bancaire',
            carte_bancaire: 'Carte bancaire',
            traite: 'Traite'
        };
        return labels[mode] || mode;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </>
        );
    }

    if (error || !reglement) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger">
                        <h4>Règlement non trouvé</h4>
                        <p>{error || "Le règlement demandé n'existe pas"}</p>
                        <Button variant="outline-danger" onClick={() => navigate('/ventes/reglements')}>
                            Retour à la liste
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h1 className="page-title mb-1">Règlement {reglement.numero_reglement}</h1>
                                    <p className="text-muted">Détails du règlement</p>
                                </div>
                                <div>
                                    <Button variant="outline-secondary" onClick={() => navigate('/ventes/reglements')} className="me-2">
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                    {reglement.statut === 'en_attente' && (
                                        <Button variant="success" onClick={() => setShowConfirmModal(true)} className="me-2">
                                            <i className="ri-check-double-line me-2"></i>Valider
                                        </Button>
                                    )}
                                    <Button variant="outline-danger" onClick={() => setShowConfirmModal(true)}>
                                        <i className="ri-delete-bin-line me-2"></i>Supprimer
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Infos principales */}
                    <Row className="g-4 mb-4">
                        <Col md={8}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0 pt-4">
                                    <h5 className="mb-0">Informations générales</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <table className="table table-borderless">
                                                <tbody>
                                                    <tr>
                                                        <th className="text-muted" width="40%">N° Règlement</th>
                                                        <td className="fw-bold">{reglement.numero_reglement}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Date</th>
                                                        <td>{formatDate(reglement.date_reglement)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Mode</th>
                                                        <td>
                                                            <i className={`${getModeIcon(reglement.mode_reglement)} me-2`}></i>
                                                            {getModeLabel(reglement.mode_reglement)}
                                                        </td>
                                                    </tr>
                                                    {reglement.reference && (
                                                        <tr>
                                                            <th className="text-muted">Référence</th>
                                                            <td>{reglement.reference}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </Col>
                                        <Col md={6}>
                                            <table className="table table-borderless">
                                                <tbody>
                                                    <tr>
                                                        <th className="text-muted" width="40%">Montant</th>
                                                        <td className="fw-bold fs-5 text-primary">{formatCurrency(reglement.montant)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Statut</th>
                                                        <td>{getStatutBadge(reglement.statut)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Saisi par</th>
                                                        <td>{reglement.prenom_utilisateur} {reglement.nom_utilisateur}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Date création</th>
                                                        <td>{formatDate(reglement.date_creation)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </Col>
                                    </Row>
                                    
                                    {reglement.commentaire && (
                                        <div className="mt-3 p-3 bg-light rounded-3">
                                            <small className="text-muted d-block mb-2">Commentaire :</small>
                                            <p className="mb-0">{reglement.commentaire}</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0 pt-4">
                                    <h5 className="mb-0">Client</h5>
                                </Card.Header>
                                <Card.Body>
                                    <h6 className="fw-bold mb-2">{reglement.client_nom}</h6>
                                    <p className="mb-1 small">
                                        <i className="ri-barcode-line me-2 text-muted"></i>
                                        Code: {reglement.code_client}
                                    </p>
                                    {reglement.client_telephone && (
                                        <p className="mb-1 small">
                                            <i className="ri-phone-line me-2 text-muted"></i>
                                            {reglement.client_telephone}
                                        </p>
                                    )}
                                    {reglement.client_email && (
                                        <p className="mb-1 small">
                                            <i className="ri-mail-line me-2 text-muted"></i>
                                            {reglement.client_email}
                                        </p>
                                    )}
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        className="w-100 mt-3"
                                        onClick={() => navigate(`/ventes/clientsDetails/${reglement.id_client}`)}
                                    >
                                        Voir la fiche client
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>   
                    </Row>         

                    {/* Facture associée */}
                    {reglement.id_facture && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pt-4">
                                <h5 className="mb-0">Facture associée</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="30%">N° Facture</th>
                                                    <td>
                                                        <Link to={`/ventes/facturesDetails/${reglement.id_facture}`} className="text-primary">
                                                            {reglement.numero_facture}
                                                        </Link>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Montant TTC</th>
                                                    <td className="fw-bold">{formatCurrency(reglement.facture_montant)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Col>
                                    <Col md={6}>
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="30%">Solde avant</th>
                                                    <td>{formatCurrency(reglement.facture_solde + reglement.montant)}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Solde après</th>
                                                    <td className="fw-bold text-primary">{formatCurrency(reglement.facture_solde)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* Modal de confirmation */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {reglement.statut === 'en_attente' ? 'Valider le règlement' : 'Supprimer le règlement'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {reglement.statut === 'en_attente' ? (
                        <p>Êtes-vous sûr de vouloir valider ce règlement ? Le solde du client et de la facture sera mis à jour.</p>
                    ) : (
                        <p>Êtes-vous sûr de vouloir supprimer ce règlement ? Cette action est irréversible.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Annuler
                    </Button>
                    <Button 
                        variant={reglement.statut === 'en_attente' ? 'success' : 'danger'} 
                        onClick={reglement.statut === 'en_attente' ? handleValider : handleSupprimer}
                        disabled={isProcessing}
                    >
                        {isProcessing ? <Spinner size="sm" /> : 'Confirmer'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}